import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  Table,
  Space,
  Input,
  Select,
  Typography,
  Row,
  Col,
  Breadcrumb,
} from "antd";
import { useSelector } from "react-redux";
import {
  FolderOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  type FileEntry,
  type FileStats,
  type FileFilterType,
  type Breadcrumb as BreadcrumbType,
} from "./interface";
import "./AllFiles.css";
import { RootState } from "store";

const { Title } = Typography;

const getFileIcon = (type?: string) => {
  switch (type?.toLowerCase()) {
    case "pdf":
      return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    case "docx":
      return <FileWordOutlined style={{ color: "#2f54eb" }} />;
    case "xlsx":
      return <FileExcelOutlined style={{ color: "#52c41a" }} />;
    default:
      return <FileOutlined style={{ color: "#595959" }} />;
  }
};

const AllFiles: React.FC = () => {
  const { loading } = useSelector((state: RootState) => state.auth);
  const [searchText, setSearchText] = useState("");
  const [nameFilter, setNameFilter] = useState<string>();
  const [qmsFilter, setQmsFilter] = useState<string>();
  const [folderFilter, setFolderFilter] = useState<string>();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbType[]>([
    { id: "root", name: "All Files" },
  ]);

  // Dummy data with subfolders - wrapped in useMemo to prevent recreation on every render
  const dummyData: FileEntry[] = useMemo(
    () => [
      {
        id: "1",
        name: "Validation Documentation",
        modified: "Yesterday at 5:00 am",
        modifiedBy: "Ashley Jones",
        isFolder: true,
        qmsElement: "QMS-123",
        folderName: "Validation",
        type: "folder",
        children: [
          {
            id: "1.1",
            name: "System Validation",
            modified: "Yesterday at 3:30 pm",
            modifiedBy: "Ashley Jones",
            isFolder: true,
            qmsElement: "QMS-123",
            folderName: "Validation",
            type: "folder",
            parentId: "1",
          },
          {
            id: "1.2",
            name: "Validation Protocol.pdf",
            modified: "Yesterday at 2:45 pm",
            modifiedBy: "Ashley Jones",
            isFolder: false,
            qmsElement: "QMS-123",
            folderName: "Validation",
            type: "pdf",
            size: "3.2 MB",
            parentId: "1",
          },
        ],
      },
      {
        id: "2",
        name: "System Configuration.pdf",
        modified: "2 days ago",
        modifiedBy: "William Brown",
        isFolder: false,
        qmsElement: "QMS-124",
        folderName: "Configuration",
        type: "pdf",
        size: "2.5 MB",
      },
      {
        id: "3",
        name: "Work Instructions.docx",
        modified: "April 31",
        modifiedBy: "Charles Thomas",
        isFolder: false,
        qmsElement: "QMS-125",
        folderName: "Instructions",
        type: "docx",
        size: "1.8 MB",
      },
    ],
    [],
  );

  // Function to get current folder data
  const getCurrentFolderData = useCallback(() => {
    if (!currentFolderId) {
      return dummyData;
    }

    // Find the current folder
    const findFolder = (
      items: FileEntry[],
      id: string,
    ): FileEntry | undefined => {
      for (const item of items) {
        if (item.id === id) {
          return item;
        }
        if (item.children) {
          const found = findFolder(item.children, id);
          if (found) return found;
        }
      }
      return undefined;
    };

    const currentFolder = findFolder(dummyData, currentFolderId);
    return currentFolder?.children || [];
  }, [currentFolderId, dummyData]);

  // Handle folder click
  const handleFolderClick = useCallback((record: FileEntry) => {
    if (record.isFolder) {
      setCurrentFolderId(record.id);
      setBreadcrumbs((prev) => [...prev, { id: record.id, name: record.name }]);
    }
  }, []);

  // Handle breadcrumb click
  const handleBreadcrumbClick = useCallback((id: string, index: number) => {
    setCurrentFolderId(id === "root" ? null : id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
  }, []);

  const fileStats: FileStats = {
    totalFiles: getCurrentFolderData().filter((item) => !item.isFolder).length,
    totalFolders: getCurrentFolderData().filter((item) => item.isFolder).length,
    totalSize: "4.3 MB",
  };

  const columns: ColumnsType<FileEntry> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (name: string, record: FileEntry) => (
        <Space>
          {record.isFolder ? (
            <FolderOutlined style={{ color: "#1890ff" }} />
          ) : (
            getFileIcon(record.type)
          )}
          <span
            className="file-name"
            onClick={() => record.isFolder && handleFolderClick(record)}
            style={{ cursor: record.isFolder ? "pointer" : "default" }}
          >
            {name}
          </span>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Modified",
      dataIndex: "modified",
      key: "modified",
      width: "15%",
      sorter: (a, b) => a.modified.localeCompare(b.modified),
    },
    {
      title: "Modified by",
      dataIndex: "modifiedBy",
      key: "modifiedBy",
      width: "15%",
    },
  ];
  const filteredData = useMemo(() => {
    const currentFolderData = getCurrentFolderData();

    return currentFolderData.filter((item) => {
      // Global search
      const searchTerm = searchText.toLowerCase();
      const matchesSearch =
        !searchText ||
        item.name.toLowerCase().includes(searchTerm) ||
        (item.qmsElement || "").toLowerCase().includes(searchTerm) ||
        (item.folderName || "").toLowerCase().includes(searchTerm);

      // Individual filters
      const matchesName =
        !nameFilter ||
        item.name.toLowerCase().includes(nameFilter.toLowerCase());

      const matchesQms =
        !qmsFilter ||
        item.qmsElement?.toLowerCase() === qmsFilter.toLowerCase();

      const matchesFolder =
        !folderFilter ||
        item.folderName?.toLowerCase() === folderFilter.toLowerCase();

      return matchesSearch && matchesName && matchesQms && matchesFolder;
    });
  }, [searchText, nameFilter, qmsFilter, folderFilter, getCurrentFolderData]);
  return (
    <div className="all-files">
      <Card>
        <div className="all-files__breadcrumb">
          <Breadcrumb
            items={breadcrumbs.map((crumb, index) => ({
              title:
                index === 0 ? (
                  <span onClick={() => handleBreadcrumbClick(crumb.id, index)}>
                    <HomeOutlined /> {crumb.name}
                  </span>
                ) : (
                  <span onClick={() => handleBreadcrumbClick(crumb.id, index)}>
                    {crumb.name}
                  </span>
                ),
            }))}
          />
        </div>
        <div className="all-files__header">
          <Title level={2}>
            {breadcrumbs[breadcrumbs.length - 1]?.name || "All Files"}
          </Title>
          <div className="all-files__filters">
            <Input.Search
              placeholder="Search this library"
              style={{ width: "18.75rem" }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
        <Row gutter={16} className="all-files__filter-bar">
          <Col>
            <span>Filters:</span>
          </Col>
          <Col>
            <Select
              placeholder="Filter by name"
              style={{ width: "12.5rem" }}
              allowClear
              showSearch
              value={nameFilter}
              onChange={setNameFilter}
              options={Array.from(new Set(dummyData.map((item) => item.name)))
                .filter(Boolean)
                .sort()
                .map((value) => ({
                  label: value,
                  value: value,
                }))}
              filterOption={(input, option) =>
                (option?.label?.toString() ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
          <Col>
            <Select
              placeholder="Filter by QMS Element"
              style={{ width: "12.5rem" }}
              allowClear
              showSearch
              value={qmsFilter}
              onChange={setQmsFilter}
              options={Array.from(
                new Set(dummyData.map((item) => item.qmsElement)),
              )
                .filter(Boolean)
                .sort()
                .map((value) => ({
                  label: value,
                  value: value,
                }))}
              filterOption={(input, option) =>
                (option?.label?.toString() ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
          <Col>
            <Select
              placeholder="Filter by Folder"
              style={{ width: "12.5rem" }}
              allowClear
              showSearch
              value={folderFilter}
              onChange={setFolderFilter}
              options={Array.from(
                new Set(dummyData.map((item) => item.folderName)),
              )
                .filter(Boolean)
                .sort()
                .map((value) => ({
                  label: value,
                  value: value,
                }))}
              filterOption={(input, option) =>
                (option?.label?.toString() ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
        </Row>{" "}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: () => record.isFolder && handleFolderClick(record),
            style: { cursor: record.isFolder ? "pointer" : "default" },
          })}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </Card>
    </div>
  );
};

export default AllFiles;
